class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    if (window.cartRecommendations) window.cartRecommendations.refresh();
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

/* ------------------------------------------------------------------
 * Anbefalinger i kurv-skuffen ("Du vil måske også kunne lide")
 * Viser anbefalinger baseret paa den senest tilfoejede vare.
 * 1) Stue-logik (sections/cart-recommendations.liquid via produktsiden):
 *    sofa -> sofabord, taeppe, lampe, puder ... (en fra hver kategori)
 * 2) Ellers Shopifys egne anbefalinger.
 * Desktop: panel til venstre for kurven. Mobil: under varerne i kurven.
 * Overskriften rettes i HEADING herunder.
 * ------------------------------------------------------------------ */
(function () {
  const HEADING = 'Du vil måske også kunne lide';
  const LIMIT = 4;
  const SECTION_ID = 'cart-recommendations';
  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const cache = {};
  let requestId = 0;

  const STYLES = `
    .cart-recs__heading { font-family: var(--font-heading-family); font-weight: var(--font-heading-weight); font-size: 1.6rem; letter-spacing: 0.3rem; text-transform: uppercase; margin: 0 0 2.4rem; color: rgb(var(--color-foreground)); }
    .cart-recs__list { display: flex; flex-direction: column; gap: 2.4rem; }
    .cart-recs__item { display: grid; grid-template-columns: 8rem 1fr; gap: 1.6rem; align-items: start; }
    .cart-recs__media { display: block; aspect-ratio: 1; overflow: hidden; background: rgba(var(--color-foreground), 0.04); }
    .cart-recs__media img { display: block; width: 100%; height: 100%; object-fit: cover; }
    .cart-recs__title { display: block; font-size: 1.4rem; line-height: 1.4; color: rgb(var(--color-foreground)); text-decoration: none; }
    .cart-recs__title:hover { text-decoration: underline; text-underline-offset: 0.3rem; }
    .cart-recs__price { font-size: 1.3rem; margin: 0.6rem 0 1rem; color: rgba(var(--color-foreground), 0.75); }
    .cart-recs__price s { margin-right: 0.6rem; opacity: 0.6; }
    .cart-recs__add { display: inline-flex; align-items: center; gap: 0.8rem; padding: 0 0 0.3rem; margin: 0; background: none; border: 0; border-bottom: 0.1rem solid rgba(var(--color-foreground), 0.4); border-radius: 0; font-family: inherit; font-size: 1.1rem; letter-spacing: 0.2rem; text-transform: uppercase; color: rgb(var(--color-foreground)); text-decoration: none; cursor: pointer; }
    .cart-recs__add:hover { border-bottom-color: rgb(var(--color-foreground)); }
    .cart-recs__add[disabled] { opacity: 0.5; cursor: default; }
    cart-drawer.is-empty .cart-recs { display: none !important; }
    .cart-recs--side { display: none; }
    .cart-recs--inline { display: none; margin: 0.5rem 0 2rem; padding-top: 2rem; border-top: 0.1rem solid rgba(var(--color-foreground), 0.08); }
    .cart-recs--inline.is-loaded { display: block; }
    .cart-recs--inline .cart-recs__heading { font-size: 1.3rem; margin-bottom: 1.6rem; }
    .cart-recs--inline .cart-recs__list { gap: 1.6rem; }
    .cart-recs--inline .cart-recs__item { grid-template-columns: 6.4rem 1fr; gap: 1.2rem; }
    @media screen and (min-width: 990px) {
      .cart-recs--inline, .cart-recs--inline.is-loaded { display: none; }
      .cart-recs--side { display: block; position: fixed; top: 0; right: 40rem; width: 30rem; height: 100%; overflow-y: auto; padding: 3.2rem 2.4rem; background-color: rgb(var(--color-background)); border-left: 0.1rem solid rgba(var(--color-foreground), 0.1); opacity: 0; visibility: hidden; transform: translateX(2rem); transition: opacity 0.25s ease, transform 0.25s ease, visibility 0s linear 0.25s; }
      cart-drawer.active .cart-recs--side.is-loaded { opacity: 1; visibility: visible; transform: none; transition: opacity 0.3s ease 0.15s, transform 0.3s ease 0.15s, visibility 0s; }
    }
  `;

  function injectStyles() {
    if (document.getElementById('cart-recs-styles')) return;
    const style = document.createElement('style');
    style.id = 'cart-recs-styles';
    style.textContent = STYLES;
    document.head.appendChild(style);
  }

  function buildContainer(modifier) {
    const el = document.createElement('aside');
    el.className = 'cart-recs cart-recs--' + modifier;
    el.setAttribute('aria-label', HEADING);
    el.innerHTML = '<h2 class="cart-recs__heading">' + HEADING + '</h2><div class="cart-recs__list"></div>';
    return el;
  }

  function ensureContainers() {
    const drawerRoot = document.getElementById('CartDrawer');
    if (!drawerRoot) return [];

    const inner = drawerRoot.querySelector('.drawer__inner');
    let side = drawerRoot.querySelector('.cart-recs--side');
    if (!side && inner) {
      side = buildContainer('side');
      inner.classList.forEach((c) => {
        if (c.indexOf('color-') === 0) side.classList.add(c);
      });
      drawerRoot.insertBefore(side, inner);
    }

    const items = drawerRoot.querySelector('cart-drawer-items');
    let inline = drawerRoot.querySelector('.cart-recs--inline');
    if (!inline && items) {
      inline = buildContainer('inline');
      items.appendChild(inline);
    }

    return [side, inline].filter(Boolean);
  }

  function render(html) {
    ensureContainers().forEach((el) => {
      el.querySelector('.cart-recs__list').innerHTML = html || '';
      el.classList.toggle('is-loaded', !!html);
    });
  }

  // Henter sektionen og vaelger op til LIMIT produkter.
  // Har produkterne en data-group (stue-logik), tages foerst et produkt fra hver kategori.
  async function fetchItems(url, inCart) {
    let text;
    try {
      const res = await fetch(url);
      if (!res.ok) return null;
      text = await res.text();
    } catch (e) {
      return null;
    }

    const doc = new DOMParser().parseFromString(text, 'text/html');
    const seen = new Set();
    const candidates = Array.from(doc.querySelectorAll('[data-cart-recs-item]')).filter((el) => {
      const pid = el.dataset.productId;
      if (inCart.has(pid) || seen.has(pid)) return false;
      seen.add(pid);
      return true;
    });

    const picked = [];
    const usedGroups = new Set();
    candidates.forEach((el) => {
      const group = el.dataset.group;
      if (picked.length < LIMIT && group && !usedGroups.has(group)) {
        usedGroups.add(group);
        picked.push(el);
      }
    });
    candidates.forEach((el) => {
      if (picked.length < LIMIT && picked.indexOf(el) === -1) picked.push(el);
    });

    return picked.map((el) => el.outerHTML).join('');
  }

  async function refresh() {
    injectStyles();
    const id = ++requestId;

    let cart;
    try {
      const res = await fetch(root + 'cart.js', { headers: { Accept: 'application/json' } });
      cart = await res.json();
    } catch (e) {
      return;
    }
    if (id !== requestId) return;

    if (!cart.items || cart.items.length === 0) {
      render('');
      return;
    }

    const inCart = new Set(cart.items.map((item) => String(item.product_id)));
    const source = cart.items[0]; // nyeste vare ligger foerst
    const key = source.product_id + '|' + Array.from(inCart).sort().join(',');

    if (cache[key] !== undefined) {
      render(cache[key]);
      return;
    }

    // 1) Stue-logik via produktets egen side
    let html = await fetchItems(root + 'products/' + source.handle + '?section_id=' + SECTION_ID, inCart);
    if (id !== requestId) return;

    // 2) Ellers Shopifys anbefalinger
    if (!html) {
      html = await fetchItems(
        root + 'recommendations/products?product_id=' + source.product_id + '&limit=10&section_id=' + SECTION_ID,
        inCart
      );
      if (id !== requestId) return;
    }

    if (html === null) return;
    cache[key] = html;
    render(html);
  }

  async function addToCart(button) {
    const drawer = document.querySelector('cart-drawer');
    const label = button.innerHTML;
    button.disabled = true;

    try {
      const res = await fetch(root + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [{ id: Number(button.dataset.variantId), quantity: 1 }],
          sections: ['cart-drawer', 'cart-icon-bubble'],
          sections_url: window.location.pathname,
        }),
      });
      const state = await res.json();
      if (!res.ok || state.status) throw new Error(state.description || state.message);

      if (drawer && typeof drawer.renderContents === 'function') {
        drawer.renderContents(state);
      } else {
        window.location.href = root + 'cart';
      }
    } catch (e) {
      button.textContent = 'Kunne ikke tilføjes';
      setTimeout(() => {
        button.innerHTML = label;
        button.disabled = false;
      }, 2500);
    }
  }

  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-cart-recs-add]');
    if (!button || button.disabled) return;
    event.preventDefault();
    addToCart(button);
  });

  // Opdater naar antal aendres / varer fjernes inde i kurven
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, () => setTimeout(refresh, 50));
  }

  window.cartRecommendations = { refresh: refresh };
})();
